/**
 * SolePropDialog — Sole Proprietor cohort wizard (Personal-with-DBA-overlay).
 *
 * Three-step wizard collapsing Deposit Pro's legacy 9-screen flow per the
 * DESIGN-BIBLE Five Pillars (Modern / Intuitive / Guiding / Responsive /
 * Custom-iconned):
 *
 *   Step 1 — Owner identity (the natural person who IS the Sole Prop):
 *            First/Middle/Last name, DOB, SSN, address, phone, email,
 *            Photo ID, citizenship status.
 *
 *   Step 2 — Business overlay:
 *            Business name (DBA), NAICS, Fictitious Name Filing # / date / state,
 *            optional business address differing from owner's home.
 *            Live Account Field 84 preview ("Bill's Bait Shop DBA").
 *
 *   Step 3 — Account preferences:
 *            POD beneficiaries (ALLOWED per memory), debit card design,
 *            Reg E opt-in / opt-out, paper-vs-electronic statements,
 *            internet banking enrollment.
 *
 * Every step shares the 2-column layout per Wave 6a modal doctrine:
 *   - LEFT  — form inputs
 *   - RIGHT — persistent guidance panel (regulatory citations, locked-field
 *             defaults, common mistakes, related catalogs)
 *
 * Wire locks (per memory project_business_procedures_full_lock_20260506):
 *   customerType = 'Individual'  Field 92 = '26'  Field 39 = 'Y'
 *   tinCode = 'S'  Field 84 = `<Business Name> DBA`
 *
 * Affordances handled:
 *   - POD beneficiaries: shown (allowed for Sole Prop).
 *   - Beneficial Owner form: HIDDEN (not required for Sole Prop).
 *   - Banking Resolution: HIDDEN (not required for Sole Prop).
 *
 * Hook-order safety: ALL hooks are declared above the `if (!open) return null`
 * gate per feedback memo `react_310_addtocartmodal_hook_order_20260508`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import { Button, DateTextInput, ModalShell, Stepper, Pill, Banner } from '@/components/primitives';
import { formatSsn } from '@/utils/ssn';
import type { RaoPerson } from '../../../types';
import { PdwStatusBadges } from '../PdwStatusBadges';
import { useSolePropDerivation } from './useSolePropDerivation';
import { withMiddleInitial } from '../shared/middleInitial';
import {
  SOLE_PROP_WIRE,
  SOLE_PROP_VALIDATION,
  SOLE_PROP_STATE_OPTIONS,
  SOLE_PROP_CITIZENSHIP_OPTIONS,
  SOLE_PROP_NAICS_HELPER,
  PHOTO_ID_TYPES,
  STATEMENT_PREF_OPTIONS,
  deriveField84,
} from './sole-prop-config';

// -----------------------------------------------------------------------------
// Shared leaf styles — visual parity with IndividualIdentitySection
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
  padding: '6px 8px',
  background: 'var(--ink-100, #eef2f6)',
  borderRadius: 4,
  display: 'inline-block',
};

const fieldRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--pcb-blue-dark, #1a406a)',
  margin: '8px 0 4px',
};

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export type SolePropDialogMode = 'review-add' | 'new' | 'edit';

export interface SolePropDialogProps {
  open: boolean;
  mode: SolePropDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  /** Required by DocumentSlotsSection on the document-loaded step (not on this wizard surface yet). */
  cartId: string;
  /** Officer code stamped on uploads. */
  officerCode?: string;
  /** When true, disables every input + hides save controls. */
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Wizard step ids
// -----------------------------------------------------------------------------

const WIZARD_STEPS: ReadonlyArray<{ id: 'owner' | 'business' | 'account'; label: string }> = [
  { id: 'owner',    label: 'Owner identity' },
  { id: 'business', label: 'Business overlay' },
  { id: 'account',  label: 'Account preferences' },
];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function SolePropDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId: _cartId,
  officerCode: _officerCode,
  readOnly = false,
}: SolePropDialogProps): JSX.Element | null {
  // --- All hooks declared above the open-gate (Rules of Hooks) -----------
  const [local, setLocal] = useState<RaoPerson>(person);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [stepIndex, setStepIndex] = useState<number>(0);

  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
    setStepIndex(0);
  }, [person]);

  const sectionMode: 'new' | 'existing' = mode === 'new' ? 'new' : 'existing';
  const derivation = useSolePropDerivation(local, { mode: sectionMode });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...withMiddleInitial(patch) };
      // Lock the canonical wire shape on every change — defensive against
      // upstream callers that pre-fill with stale customerType / callClass.
      next.customerType = SOLE_PROP_WIRE.customerType;
      next.callClass = SOLE_PROP_WIRE.callClass;
      next.tinCode = SOLE_PROP_WIRE.tinCode;

      // Keep fullName in sync (Personal-style: owner's first+last).
      const fn = next.firstName ?? '';
      const mn = next.middleName ?? '';
      const ln = next.lastName ?? '';
      const display = [fn, mn, ln].filter((x) => !!x).join(' ').trim();
      if (display) next.fullName = display;

      // Mark existing CIFs dirty so commit pipeline branches to CustMod.
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  const patchExtra = useCallback((patch: Record<string, unknown>): void => {
    setLocal((prev) => ({ ...prev, extra: { ...(prev.extra ?? {}), ...patch } }));
    setHasChanges(true);
  }, []);

  // ---- Validation per step ---------------------------------------------
  const ownerStepValid = useMemo<boolean>(() => {
    return Boolean(
      (local.firstName ?? '').trim() &&
        (local.lastName ?? '').trim() &&
        (local.dob ?? '').trim() &&
        (local.taxId ?? '').replace(/\D/g, '').length === SOLE_PROP_VALIDATION.ssnLen &&
        (local.addr ?? '').trim() &&
        (local.city ?? '').trim() &&
        (local.state ?? '').trim() &&
        (local.zip ?? '').trim(),
    );
  }, [local.firstName, local.lastName, local.dob, local.taxId, local.addr, local.city, local.state, local.zip]);

  const businessStepValid = useMemo<boolean>(() => {
    const bizName = (local.name2 ?? '').trim();
    const naics = (local.naicsCode ?? '').trim();
    const filingNo = String(local.extra?.fictitiousFilingNumber ?? '').trim();
    const filingDate = String(local.extra?.fictitiousFilingDate ?? '').trim();
    const filingState = String(local.extra?.fictitiousFilingState ?? '').trim();
    return Boolean(
      bizName &&
        bizName.length <= SOLE_PROP_VALIDATION.businessNameMaxLen &&
        naics.length === 6 &&
        filingNo &&
        filingDate &&
        filingState,
    );
  }, [
    local.name2,
    local.naicsCode,
    local.extra?.fictitiousFilingNumber,
    local.extra?.fictitiousFilingDate,
    local.extra?.fictitiousFilingState,
  ]);

  const accountStepValid = useMemo<boolean>(() => {
    // Step 3 has only opt-in choices — all of which have safe defaults.
    return true;
  }, []);

  const currentStepValid =
    stepIndex === 0 ? ownerStepValid : stepIndex === 1 ? businessStepValid : accountStepValid;

  // ---- Save guard -------------------------------------------------------
  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';

  const allStepsValid = ownerStepValid && businessStepValid && accountStepValid;
  const saveDisabled = dupInCart || !allStepsValid || readOnly;

  const displayName = derivation.displayName || '(new Sole Proprietor)';
  const title =
    mode === 'review-add'
      ? `Review & add — ${displayName}`
      : mode === 'new'
        ? 'Add Sole Proprietor'
        : `Edit Sole Proprietor — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? hasChanges ? 'Add existing w/ changes' : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  const isLastStep = stepIndex === WIZARD_STEPS.length - 1;
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <div style={{ display: 'flex', gap: 8 }}>
        {stepIndex > 0 && (
          <Button variant="ghost" onClick={() => setStepIndex((i) => Math.max(0, i - 1))}>
            Back
          </Button>
        )}
        {!isLastStep && (
          <Button
            variant="primary"
            onClick={() => setStepIndex((i) => Math.min(WIZARD_STEPS.length - 1, i + 1))}
            disabled={!currentStepValid}
          >
            Next
          </Button>
        )}
        {isLastStep && (
          <Button variant="primary" onClick={() => onSave(local)} disabled={saveDisabled}>
            {btnLabel}
          </Button>
        )}
      </div>
    </div>
  );

  // --- Early return AFTER all hooks declared -----------------------------
  if (!open) return null;

  // ---------------------------------------------------------------------------
  // Two-column layout: form (left) + guidance aside (right)
  // ---------------------------------------------------------------------------
  const layout = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 280px',
        gap: 24,
        alignItems: 'start',
      }}
    >
      <div>
        <Stepper
          steps={WIZARD_STEPS.map((s) => ({ label: s.label }))}
          currentIndex={stepIndex}
          variant="apply"
        />

        <div style={{ marginTop: 16 }}>
          {stepIndex === 0 && (
            <OwnerIdentityStep person={local} onChange={update} readOnly={readOnly} />
          )}
          {stepIndex === 1 && (
            <BusinessOverlayStep
              person={local}
              onChange={update}
              patchExtra={patchExtra}
              readOnly={readOnly}
              acctField84Preview={derivation.acctField84Preview}
            />
          )}
          {stepIndex === 2 && (
            <AccountPreferencesStep
              person={local}
              onChange={update}
              patchExtra={patchExtra}
              readOnly={readOnly}
            />
          )}
        </div>
      </div>

      <SolePropGuidancePanel step={WIZARD_STEPS[stepIndex].id} derivation={derivation} />
    </div>
  );

  return (
    <ModalShell open={open} onClose={onClose} title={title} footer={footer} size="lg">
      {dupInCart && (
        <Banner tone="warn">
          CIF <strong>{local.cifId}</strong> is already on this cart — use Edit to update their record.
        </Banner>
      )}
      {/* D3 convergence — canonical 4-badge status row. Sole-Prop IS a natural
          person (owner SSN / citizenship / photo-ID), so the badges are
          meaningful. Sole-Prop deliberately KEEPS its purpose-built 3-step
          DBA-overlay wizard (its entity-appropriate flow per design) rather
          than the tab strip — a Stepper + a tab strip are conflicting nav
          paradigms; the "or an entity-appropriate tab subset" latitude in the
          slot prompt applies here. */}
      <div style={{ marginBottom: 12 }}>
        <PdwStatusBadges person={local} />
      </div>
      {layout}
    </ModalShell>
  );
}

export default SolePropDialog;

// =============================================================================
// Step 1 — Owner identity
// =============================================================================

function OwnerIdentityStep(props: {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  readOnly: boolean;
}): JSX.Element {
  const { person, onChange, readOnly } = props;
  const ssnPreview = formatSsn(person.taxId ?? '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={sectionTitleStyle}>The person who owns the business</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
        Sole Proprietorships use the <strong>owner&apos;s personal information</strong> on file with
        Core Director. The business name is added as an overlay on the account in the next step.
      </div>

      <div style={fieldRow}>
        <div>
          <label style={labelStyle}>First name *</label>
          <input
            style={inputStyle}
            value={person.firstName ?? ''}
            onChange={(e) => onChange({ firstName: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Middle initial</label>
          <input
            style={inputStyle}
            value={person.middleName ?? ''}
            onChange={(e) => onChange({ middleName: e.currentTarget.value })}
            maxLength={1}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Last name *</label>
          <input
            style={inputStyle}
            value={person.lastName ?? ''}
            onChange={(e) => onChange({ lastName: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
      </div>

      <div style={fieldRow}>
        <div>
          <label style={labelStyle} htmlFor="rao-sp-dob">Date of birth *</label>
          {/* Typed MM/DD/YYYY (Collette Cruse 2026-10-16: number-keypad entry). */}
          <DateTextInput
            id="rao-sp-dob"
            style={inputStyle}
            value={person.dob ?? ''}
            onChange={(iso) => onChange({ dob: iso })}
            disabled={readOnly}
            title="Date of birth"
          />
        </div>
        <div>
          <label style={labelStyle}>SSN * (owner&apos;s Social Security Number)</label>
          <input
            style={inputStyle}
            value={person.taxId ?? ''}
            onChange={(e) => onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })}
            disabled={readOnly}
            placeholder="XXXXXXXXX"
            maxLength={9}
          />
          {ssnPreview && (
            <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>
              Preview: <span style={monoStyle}>{ssnPreview}</span>
            </div>
          )}
        </div>
        <div>
          <label style={labelStyle}>Citizenship status</label>
          <select
            style={inputStyle}
            value={person.citizenshipStatus ?? ''}
            onChange={(e) =>
              onChange({ citizenshipStatus: e.currentTarget.value as RaoPerson['citizenshipStatus'] })
            }
            disabled={readOnly}
          >
            {!person.citizenshipStatus && (
              <option value="" disabled>— select —</option>
            )}
            {SOLE_PROP_CITIZENSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={sectionTitleStyle}>Owner&apos;s home address</div>
      <div style={fieldRow}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Street address *</label>
          <input
            style={inputStyle}
            value={person.addr ?? ''}
            onChange={(e) => onChange({ addr: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>City *</label>
          <input
            style={inputStyle}
            value={person.city ?? ''}
            onChange={(e) => onChange({ city: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>State *</label>
          <select
            style={inputStyle}
            value={person.state ?? ''}
            onChange={(e) => onChange({ state: e.currentTarget.value })}
            disabled={readOnly}
          >
            <option value="">— Pick a state —</option>
            {SOLE_PROP_STATE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>ZIP *</label>
          <input
            style={inputStyle}
            value={person.zip ?? ''}
            onChange={(e) => onChange({ zip: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
      </div>

      <div style={sectionTitleStyle}>Contact</div>
      <div style={fieldRow}>
        <div>
          <label style={labelStyle}>Cell phone</label>
          <input
            style={inputStyle}
            value={person.phoneCell ?? ''}
            onChange={(e) => onChange({ phoneCell: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Home phone</label>
          <input
            style={inputStyle}
            value={person.phoneHome ?? ''}
            onChange={(e) => onChange({ phoneHome: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            value={person.email ?? ''}
            onChange={(e) => onChange({ email: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
      </div>

      <div style={sectionTitleStyle}>Photo ID</div>
      <div style={fieldRow}>
        <div>
          <label style={labelStyle}>ID type</label>
          <select
            style={inputStyle}
            value={String(person.extra?.photoIdType ?? '')}
            onChange={(e) =>
              onChange({ extra: { ...(person.extra ?? {}), photoIdType: e.currentTarget.value } })
            }
            disabled={readOnly}
          >
            <option value="">— Pick ID type —</option>
            {PHOTO_ID_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>ID number</label>
          <input
            style={inputStyle}
            value={String(person.extra?.photoIdNumber ?? '')}
            onChange={(e) =>
              onChange({ extra: { ...(person.extra ?? {}), photoIdNumber: e.currentTarget.value } })
            }
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Issue date</label>
          <input
            type="date"
            style={inputStyle}
            value={String(person.extra?.photoIdIssueDate ?? '')}
            onChange={(e) =>
              onChange({ extra: { ...(person.extra ?? {}), photoIdIssueDate: e.currentTarget.value } })
            }
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Expiration date</label>
          <input
            type="date"
            style={inputStyle}
            value={String(person.extra?.photoIdExpireDate ?? '')}
            onChange={(e) =>
              onChange({ extra: { ...(person.extra ?? {}), photoIdExpireDate: e.currentTarget.value } })
            }
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step 2 — Business overlay
// =============================================================================

function BusinessOverlayStep(props: {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  patchExtra: (patch: Record<string, unknown>) => void;
  readOnly: boolean;
  acctField84Preview: string;
}): JSX.Element {
  const { person, onChange, patchExtra, readOnly, acctField84Preview } = props;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={sectionTitleStyle}>Business name (DBA)</div>

      <div>
        <label style={labelStyle}>
          Business name *{' '}
          <span style={{ color: 'var(--ink-500)', fontWeight: 400 }}>
            (the name that prints on the account)
          </span>
        </label>
        <input
          style={inputStyle}
          value={person.name2 ?? ''}
          onChange={(e) => onChange({ name2: e.currentTarget.value })}
          maxLength={SOLE_PROP_VALIDATION.businessNameMaxLen}
          disabled={readOnly}
          placeholder="e.g., Bill's Bait Shop"
        />
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Account Field 84 (prints on statements / checks):</span>
          <span style={monoStyle}>{acctField84Preview || '— enter business name —'}</span>
        </div>
      </div>

      <div style={fieldRow}>
        <div>
          <label style={labelStyle}>
            NAICS code * <span style={{ color: 'var(--ink-500)', fontWeight: 400 }}>(6-digit industry code)</span>
          </label>
          <input
            style={inputStyle}
            value={person.naicsCode ?? ''}
            onChange={(e) =>
              onChange({ naicsCode: e.currentTarget.value.replace(/\D/g, '').slice(0, 6) })
            }
            disabled={readOnly}
            placeholder="722511"
            maxLength={6}
          />
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4, lineHeight: 1.4 }}>
            {SOLE_PROP_NAICS_HELPER}
          </div>
        </div>
      </div>

      <div style={sectionTitleStyle}>Fictitious Name Filing (Missouri RSMo §417.200)</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
        Sole Proprietors using a name other than their legal name must register
        a Fictitious Name Filing with the Secretary of State. This filing serves
        as the CIP identification source (CIP Field 254 = &quot;6&quot;).
      </div>

      <div style={fieldRow}>
        <div>
          <label style={labelStyle}>Filing number *</label>
          <input
            style={inputStyle}
            value={String(person.extra?.fictitiousFilingNumber ?? '')}
            onChange={(e) => patchExtra({ fictitiousFilingNumber: e.currentTarget.value })}
            disabled={readOnly}
            maxLength={SOLE_PROP_VALIDATION.fictitiousFilingNumberMaxLen}
          />
        </div>
        <div>
          <label style={labelStyle}>Filing state *</label>
          <select
            style={inputStyle}
            value={String(person.extra?.fictitiousFilingState ?? '')}
            onChange={(e) => patchExtra({ fictitiousFilingState: e.currentTarget.value })}
            disabled={readOnly}
          >
            <option value="">— Pick a state —</option>
            {SOLE_PROP_STATE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Filing date *</label>
          <input
            type="date"
            style={inputStyle}
            value={String(person.extra?.fictitiousFilingDate ?? '')}
            onChange={(e) => patchExtra({ fictitiousFilingDate: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
      </div>

      <div style={sectionTitleStyle}>Business address (optional — only if different from owner&apos;s home)</div>
      <div style={fieldRow}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Street address</label>
          <input
            style={inputStyle}
            value={String(person.extra?.businessAddr ?? '')}
            onChange={(e) => patchExtra({ businessAddr: e.currentTarget.value })}
            disabled={readOnly}
            placeholder="Leave blank to use owner's home address"
          />
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input
            style={inputStyle}
            value={String(person.extra?.businessCity ?? '')}
            onChange={(e) => patchExtra({ businessCity: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>State</label>
          <select
            style={inputStyle}
            value={String(person.extra?.businessState ?? '')}
            onChange={(e) => patchExtra({ businessState: e.currentTarget.value })}
            disabled={readOnly}
          >
            <option value="">—</option>
            {SOLE_PROP_STATE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>{s.code}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>ZIP</label>
          <input
            style={inputStyle}
            value={String(person.extra?.businessZip ?? '')}
            onChange={(e) => patchExtra({ businessZip: e.currentTarget.value })}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step 3 — Account preferences
// =============================================================================

function AccountPreferencesStep(props: {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  patchExtra: (patch: Record<string, unknown>) => void;
  readOnly: boolean;
}): JSX.Element {
  const { person, patchExtra, readOnly } = props;

  const orderDebitCard = Boolean(person.extra?.orderDebitCard);
  const orderChecks = Boolean(person.extra?.orderChecks);
  const internetBanking = Boolean(person.extra?.internetBankingEnroll);
  const regEPref = String(person.extra?.regEPref ?? '');
  const statementPref = String(person.extra?.statementPref ?? 'electronic');
  // Default 101 = Blue Card (Collette 2026-07-16), matching CardsForSection's
  // DEFAULT_CARD_CODE. This said '105', which under the pre-seed-140 (wrong) mapping WAS
  // the blue Main Bank card — the intent was always "default to the standard blue card".
  // After the correction 105 is White Hex, so leaving it would have silently defaulted
  // sole props to a themed card and made the two surfaces disagree about the default.
  const cardDesignCode = String(person.extra?.debitCardDesignCode ?? '101');

  // Read the design library from config — never a hardcoded list. The previous inline
  // <option> list was a copy of an unsourced guess: every label was wrong but Dog, and
  // when seed 140 corrected the catalog the copy inverted (it called 105 "Main Bank Blue
  // PCB" while 105 became White Hex). Duplicated catalog data is guaranteed to drift.
  //
  // A DESIGN is an active row carrying artwork (101/103/104/105). Rows 9/11 are card
  // PROGRAMS (Personal/Business Debit) with no art — the product, not a choice.
  // No category filter: Collette 2026-07-16 — "Business Accounts can choose any design."
  // A sole prop is a business, and every design row is categorised Personal, so
  // filtering by category would leave this list EMPTY.
  const { config } = useConfig();
  const cardDesigns = useMemo(() => {
    const rows = (config.cardTypes ?? []) as unknown as ReadonlyArray<Record<string, unknown>>;
    const out = rows
      .filter((r) => r['isActive'] !== false && (r['hasImage'] === true || !!r['imagePath']))
      .map((r) => ({
        code: String(r['code'] ?? ''),
        label: typeof r['description'] === 'string' && r['description']
          ? (r['description'] as string)
          : `Design ${String(r['code'] ?? '')}`,
      }))
      .filter((d) => d.code);
    // Never render an EMPTY design select: config may not have landed yet, and the
    // hardcoded list this replaced always had options. Fall back to the default alone —
    // 'Blue Card' is a cited label (Collette), not a placeholder invented here.
    return out.length > 0 ? out : [{ code: '101', label: 'Blue Card' }];
  }, [config.cardTypes]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={sectionTitleStyle}>POD beneficiaries (allowed for Sole Proprietors)</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
        Payable-On-Death beneficiaries are allowed on Sole Proprietor accounts —
        the funds belong to the owner personally. Add beneficiaries on the
        Cart Review screen after this person is added to the cart.
      </div>

      <div style={sectionTitleStyle}>Debit card</div>
      <div style={fieldRow}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={orderDebitCard}
            onChange={(e) => patchExtra({ orderDebitCard: e.currentTarget.checked })}
            disabled={readOnly}
          />
          Order a debit card for the owner
        </label>
        {orderDebitCard && (
          <div>
            <label style={labelStyle}>Card design</label>
            <select
              style={inputStyle}
              value={cardDesignCode}
              onChange={(e) => patchExtra({ debitCardDesignCode: e.currentTarget.value })}
              disabled={readOnly}
            >
              {/* Live from /v3/config/card-types. 102 (Black) excludes itself by being
                  inactive — discontinued designs are never orderable on a NEW account;
                  they surface only in the reissue picker, where a customer may hold one. */}
              {cardDesigns.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.code} — {d.label}
                  {d.code === '101' ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={sectionTitleStyle}>Order checks</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={orderChecks}
          onChange={(e) => patchExtra({ orderChecks: e.currentTarget.checked })}
          disabled={readOnly}
        />
        Place an initial check order
      </label>

      <div style={sectionTitleStyle}>Regulation E — Opt-In / Opt-Out</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
        Regulation E controls whether the bank may authorize ATM and one-time
        debit card transactions that overdraw the account (for a fee). The
        customer must explicitly choose.
      </div>
      <div style={fieldRow}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="radio"
            name="regE"
            checked={regEPref === 'opt-in'}
            onChange={() => patchExtra({ regEPref: 'opt-in' })}
            disabled={readOnly}
          />
          Opt-in (allow ATM / one-time debit overdrafts)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="radio"
            name="regE"
            checked={regEPref === 'opt-out'}
            onChange={() => patchExtra({ regEPref: 'opt-out' })}
            disabled={readOnly}
          />
          Opt-out (decline ATM / one-time debit overdrafts)
        </label>
      </div>

      <div style={sectionTitleStyle}>Statement preference</div>
      <div style={fieldRow}>
        {STATEMENT_PREF_OPTIONS.map((o) => (
          <label
            key={o.value}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: 10,
              border: `1px solid ${statementPref === o.value ? 'var(--pcb-blue, #2968ad)' : 'var(--ink-200)'}`,
              borderRadius: 6,
              cursor: 'pointer',
              background: statementPref === o.value ? 'var(--field-changed-bg, #f0f7ff)' : 'var(--surface-card, #fff)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
              <input
                type="radio"
                name="stmtPref"
                checked={statementPref === o.value}
                onChange={() => patchExtra({ statementPref: o.value })}
                disabled={readOnly}
              />
              {o.label}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>{o.hint}</span>
          </label>
        ))}
      </div>

      <div style={sectionTitleStyle}>Internet banking</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={internetBanking}
          onChange={(e) => patchExtra({ internetBankingEnroll: e.currentTarget.checked })}
          disabled={readOnly}
        />
        Enroll owner in NetTeller online banking
      </label>
      <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
        Optional — the owner can also enroll later through online self-service.
      </div>
    </div>
  );
}

// =============================================================================
// Right-side guidance panel
// =============================================================================

function SolePropGuidancePanel(props: {
  step: 'owner' | 'business' | 'account';
  derivation: ReturnType<typeof useSolePropDerivation>;
}): JSX.Element {
  const { step, derivation } = props;

  return (
    <aside
      style={{
        position: 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 14,
        background: 'var(--surface-subtle, #f7f9fc)',
        border: '1px solid var(--ink-200, #e0e6ee)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--ink-800)',
        lineHeight: 1.45,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--pcb-blue-dark)', marginBottom: 6 }}>
          Sole Proprietor — what this drives
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          <Pill variant="info">Personal cohort</Pill>
          <Pill variant="info">DBA overlay</Pill>
          <Pill variant="muted">Call Class {derivation.callClass}</Pill>
          <Pill variant="muted">Field 39 = {derivation.cifField39}</Pill>
          <Pill variant="muted">TIN code {derivation.tinCode}</Pill>
        </div>
        <div>
          One CIF (owner&apos;s SSN). Account Field 84 receives the DBA overlay so
          statements and checks print with the business name. POD beneficiaries
          are <strong>allowed</strong>; Beneficial Ownership and Banking Resolution
          are <strong>not required</strong>.
        </div>
      </div>

      {step === 'owner' && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Regulatory citations</div>
          <ul style={{ paddingLeft: 16, margin: 0 }}>
            <li>USA PATRIOT Act §326 — CIP for individuals</li>
            <li>26 USC §6109 — TIN furnished by the owner</li>
            <li>IRS Schedule C — Sole Prop reports business income on personal return</li>
          </ul>
        </div>
      )}

      {step === 'business' && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Regulatory citations</div>
          <ul style={{ paddingLeft: 16, margin: 0 }}>
            <li>RSMo §417.200 — Missouri Fictitious Name Registration Act</li>
            <li>CIP Field 254 = <code>{SOLE_PROP_WIRE.cipField254}</code> (Fictitious Name Filing)</li>
            <li>Account Field 84 format: <code>&lt;Name&gt;{SOLE_PROP_WIRE.field84Suffix}</code></li>
          </ul>
        </div>
      )}

      {step === 'account' && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Related catalogs</div>
          <ul style={{ paddingLeft: 16, margin: 0 }}>
            <li>Card Types — debit card designs (Admin)</li>
            <li>CD Penalty Rules — IRA / time deposit (Admin)</li>
            <li>NAICS Codes — industry catalog (Admin)</li>
          </ul>
        </div>
      )}

      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Common mistakes</div>
        <ul style={{ paddingLeft: 16, margin: 0 }}>
          <li>Confusing Sole Prop with LLC — Sole Prop has no separate legal entity.</li>
          <li>Using the business&apos;s EIN — Sole Prop CIF uses the owner&apos;s SSN.</li>
          <li>Forgetting the Fictitious Name Filing — required CIP evidence.</li>
        </ul>
      </div>

      <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
        Design Bible Pillars addressed: <strong>Modern</strong> (3-step vs 9-screen legacy),
        <strong> Intuitive</strong> (cohort picker → wizard → cart),
        <strong> Guiding</strong> (live Field 84 preview + this panel),
        <strong> Responsive</strong> (mobile-friendly inputs),
        <strong> Custom-iconned</strong> (cohort tile in EntityCategoryPicker).
      </div>
    </aside>
  );
}

// -----------------------------------------------------------------------------
// `deriveField84` re-export for tests / callers who want the pure helper.
// -----------------------------------------------------------------------------

export { deriveField84 };
